import {
  useGetEnrollmentMultiVerifyIdentityData,
  useSubmitEnrollmentMultiVerifyIdentityData,
  ResidencyStatus,
  CountryCode,
} from '@egw/api'
import { Alert, AlertType, Button, GenericError, Input, ProgressBar, RadioGroup, Select } from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { EyeIcon } from '@heroicons/react/20/solid'
import { useEffect, useMemo, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { dobIsValid, formatDateForAPI } from '../../../utils/form-validation'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { getTestId, HtmlTestTypes } from '../../../utils/testing/get-test-id'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'

const log = new Logger('Enrollment - Multi - VerifyIdentity')

interface FormData {
  ssn: string
  dateOfBirth: string
  residencyStatus: ResidencyStatus
  citizenshipCountries: CountryCode
}

export const VerifyIdentityPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentMultiVerifyIdentityData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiVerifyIdentityData()

  // Protect route
  useValidateRoute(data?.path)

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''
  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.progress : undefined
  const _dateOfBirth = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.dateOfBirth : ''
  const _residencyStatus = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.residencyStatus : ''
  const _citizenshipCountries =
    data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.citizenshipCountries : []

  const [isHide, setIsHide] = useState(true)
  const [hasShownIntent, setHasShownIntent] = useState<boolean>(false)
  const [hasFormInitialized, setHasFormInitialized] = useState(false)

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isValid, errors: formErrors, touchedFields },
  } = useForm({
    defaultValues: {
      ssn: '',
      dateOfBirth: '',
      residencyStatus: '',
      citizenshipCountries: '',
    },
    mode: 'onChange',
  })

  // Protect route
  useValidateRoute(data?.path)

  const ssn = watch('ssn')
  const residencyStatus = watch('residencyStatus')
  // These values are strings because we shouldn't compare ENUM to string
  const showCitizenshipCountries =
    residencyStatus === 'US_PERMANENT_RESIDENT' || residencyStatus === 'US_TEMPORARY_RESIDENT'

  useEffect(() => {
    if (!hasShownIntent && ssn && residencyStatus) {
      setHasShownIntent(true)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_IDENTITY_INTENT, {
        applicationId,
      })
    }
  }, [applicationId, hasShownIntent, ssn, residencyStatus])

  // Update form values when data is loaded
  useEffect(() => {
    if (data?.__typename === 'SubmitEnrollmentSuccess' && data.enrollmentData && !hasFormInitialized) {
      reset({
        dateOfBirth: _dateOfBirth
          ? `${_dateOfBirth.split('-')[1]}/${_dateOfBirth.split('-')[2]}/${_dateOfBirth.split('-')[0]}`
          : '',
        residencyStatus: _residencyStatus || '',
        citizenshipCountries: _citizenshipCountries?.[0] || '',
      })
      setHasFormInitialized(true)
    }
  }, [data, reset, hasFormInitialized])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const response = await mutate({
          ssn: formData.ssn,
          dateOfBirth: formatDateForAPI(formData.dateOfBirth),
          residencyStatus: formData.residencyStatus,
          citizenshipCountries: formData.citizenshipCountries ? [formData.citizenshipCountries] : [],
        })
        sendEvent('bankmulti_enroll_identity_intent', {
          applicationId,
        })
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          sendEvent('user_enroll_identity_verified', {
            applicationId,
          })
          void navigate(response.path)
        } else if (!response?.path) {
          // If no path from BE, redirect to error
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  const residencyStatusOptions = useMemo(() => {
    return Object.keys(ResidencyStatus).map((key) => ({
      value: key,
      label: t(`multi.verify-identity.citizen_enum.${key}`),
    }))
  }, [])

  const citizenshipCountriesOptions = useMemo(() => {
    return Object.keys(CountryCode).map((key) => ({
      value: key,
      label: t(`multi.verify-identity.country_code.${key}`),
    }))
  }, [])

  return (
    <EnrollmentMultiLayout title={t('multi.verify-identity.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {/* AUTO GENERATED COMPONENTS START*/}
          <PageHeader className="mb-xs" title={t('multi.verify-identity.title')} />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />

          <p className="typo-primary-base text-text-meta mb-xl">{t('multi.verify-identity.description')}</p>

          <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
            {/* Date of Birth */}
            <p className="typo-primary-base-semibold mb-2xs">{t('multi.verify-identity.date_of_birth')}</p>
            <p className="typo-primary-base text-text-meta mb-sm">
              {t('multi.verify-identity.date_of_birth_description')}
            </p>
            <Controller
              name="dateOfBirth"
              control={control}
              rules={{
                required: t('multi.verify-identity.dob_error'),
                validate: (e) => dobIsValid(e ?? '') || t('multi.verify-identity.dob_error'),
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <Input
                  {...field}
                  value={value}
                  onChange={(e) => {
                    const input = e.target.value.replace(/\D/g, '')
                    let formatted = ''

                    if (input.length > 0) {
                      formatted = input.slice(0, 2)
                      if (input.length > 2) {
                        formatted += `/${input.slice(2, 4)}`
                        if (input.length > 4) {
                          formatted += `/${input.slice(4, 8)}`
                        }
                      }
                    }

                    if (formatted.length <= 10) {
                      onChange(formatted)
                    }
                  }}
                  label={t('hycs.personal_info.dob_label')}
                  required
                  error={touchedFields.dateOfBirth ? formErrors.dateOfBirth?.message : undefined}
                  type="text"
                  className="mb-xl"
                  testId={getTestId('dateOfBirth', HtmlTestTypes.input)}
                  isPII
                />
              )}
            />

            {/* SSN */}
            <p className="typo-primary-base-semibold mb-sm">{t('multi.verify-identity.ssn')}</p>
            <Controller
              name="ssn"
              control={control}
              rules={{
                required: t('common.required_field'),
                pattern: {
                  value: /^[0-9]{9}$/,
                  message: t('common.invalid_ssn'),
                },
              }}
              render={({ field: { ...restField } }) => (
                <Input
                  {...restField}
                  label={t('multi.verify-identity.itin')}
                  maxLength={9}
                  inputMode="numeric"
                  onChange={(e) => {
                    const newValue = e.target.value.replace(/[^0-9]/g, '')
                    restField.onChange(newValue)
                  }}
                  icon={
                    <EyeIcon
                      onClick={() => {
                        setIsHide(!isHide)
                      }}
                      data-testid="ssn_input_icon"
                      className="h-5 w-5 cursor-pointer pointer-events-auto"
                    />
                  }
                  required
                  type={isHide ? 'password' : 'text'}
                  testId="ssn_input"
                  className="mb-xl"
                  error={touchedFields.ssn ? formErrors.ssn?.message : undefined}
                  isPII
                />
              )}
            />

            <p className="typo-primary-base-semibold mb-sm">{t('multi.verify-identity.citizen')}</p>
            <Controller
              name="residencyStatus"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value ?? ''}
                  options={residencyStatusOptions}
                  name="residencyStatus"
                  testId="residencyStatus_checkbox"
                  className="!py-xs"
                />
              )}
            />

            {showCitizenshipCountries ? (
              <>
                <p className="typo-primary-base-semibold mb-sm mt-xl">
                  {t('multi.verify-identity.country_of_citizenship')}
                </p>

                <Controller
                  name="citizenshipCountries"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={citizenshipCountriesOptions}
                      label={t('multi.verify-identity.country_of_citizenship_label')}
                      required
                      error={touchedFields.citizenshipCountries ? formErrors.citizenshipCountries?.message : undefined}
                      className="w-full"
                      testId="citizenshipCountries_input"
                    />
                  )}
                />
              </>
            ) : null}

            <div className="flex-1 md:hidden" />

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
